import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import dayjs from "dayjs";

import {
  useGetContractInvoicesQuery,
  useUpdateContractInvoicePaymentMutation,
} from "../../../Api/attoDeskApi";
import { IContractInvoice } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import { fmtCustomerName } from "../../../Core/utils/nameUtils";

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (v: any) => `$${(parseFloat(String(v || 0)) || 0).toFixed(2)}`;

// ── Stepper styles (match WorkOrder/History) ───────────────────────────────────
const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 18 },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: { backgroundColor: "#2f3891" },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: { backgroundColor: "#2f3891" },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 6,
    border: 0,
    backgroundColor: theme.palette.grey[300],
    borderRadius: 1,
  },
}));

const STEPS = ["Draft", "Finalized", "Paid"];

const activeStepOf = (inv: IContractInvoice): number => {
  const balance = Math.max(0, parseFloat(String(inv.grand_total ?? 0)) - parseFloat(String(inv.amount_paid ?? 0)));
  const paid = balance <= 0 && parseFloat(String(inv.amount_paid ?? 0)) > 0;
  if (paid) return 2;
  if (inv.status >= 2) return 1;
  return 0;
};

// ── Component ──────────────────────────────────────────────────────────────────
const ViewContractInvoice: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showMessage } = useNotifier();

  const [payOpen, setPayOpen] = useState(false);
  const [payData, setPayData] = useState({
    date: new Date().toLocaleDateString("en-CA"),
    amount: "",
    method: "",
    description: "",
  });
  const [payingLocal, setPayingLocal] = useState(false);

  const { data, isFetching, refetch } = useGetContractInvoicesQuery(
    { id: id ?? "" },
    { skip: !id }
  );
  const [updatePayment] = useUpdateContractInvoicePaymentMutation();

  const invoice: IContractInvoice | null = ((data?.data as any[]) ?? [])[0] ?? null;

  const handleSavePayment = async () => {
    if (!invoice || !payData.amount || !payData.method) {
      showMessage("Please fill in amount and payment method", "error");
      return;
    }
    const entered = parseFloat(payData.amount);
    if (isNaN(entered) || entered <= 0) {
      showMessage("Please enter a valid payment amount", "error");
      return;
    }
    const currentPaid = parseFloat(String(invoice.amount_paid ?? 0));
    const balance = Math.max(0, parseFloat(String(invoice.grand_total ?? 0)) - currentPaid);
    if (entered > balance + 0.001) {
      showMessage(`Amount exceeds balance (${fmt(balance)})`, "error");
      return;
    }
    setPayingLocal(true);
    try {
      await updatePayment({ id: invoice.id, amount_paid: currentPaid + entered }).unwrap();
      showMessage("Payment recorded successfully", "success");
      refetch();
      setPayOpen(false);
      setPayData({ date: new Date().toLocaleDateString("en-CA"), amount: "", method: "", description: "" });
    } catch {
      showMessage("Failed to record payment", "error");
    } finally {
      setPayingLocal(false);
    }
  };

  // ── Loading / not found ───────────────────────────────────────────────────────
  if (isFetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress sx={{ color: "#2f3891" }} />
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Invoice not found.</Typography>
        <Button sx={{ mt: 2, textTransform: "none" }} onClick={() => navigate("/contract-invoice/list")}>
          Back to list
        </Button>
      </Box>
    );
  }

  const handlePrint = () => {
    // Open window synchronously — avoids popup blocker.
    const win = window.open("", "_blank", "width=900,height=750");
    if (!win) return;

    win.document.write(`<!DOCTYPE html><html><head><title>Contract Invoice</title>
      <style>
        body { font-family: Arial, sans-serif; display: flex; align-items: center;
               justify-content: center; height: 100vh; margin: 0;
               color: #64748b; font-size: 14px; background: #f8fafc; }
        .spinner { border: 3px solid #e2e8f0; border-top-color: #1e2751;
                   border-radius: 50%; width: 32px; height: 32px;
                   animation: spin 0.8s linear infinite; margin-right: 12px; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .msg { display: flex; align-items: center; }
      </style></head>
      <body><div class="msg"><div class="spinner"></div><span>Generating invoice, please wait…</span></div></body></html>`);
    win.document.close();

    const token = localStorage.getItem("token") || "";
    const apiUrl = process.env.REACT_APP_API_URL;

    fetch(`${apiUrl}/pdf/contract-invoice/${invoice.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Server returned " + res.status);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        win.location.href = blobUrl;
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      })
      .catch(() => {
        if (!win.closed) {
          win.document.open();
          win.document.write(`<!DOCTYPE html><html><body style="font-family:Arial;padding:40px;color:#dc2626;">
            <h3>Failed to generate invoice</h3><p>Please try again or contact support.</p>
            </body></html>`);
          win.document.close();
        }
      });
  };

  const activeStep = activeStepOf(invoice);
  const balance = Math.max(0, parseFloat(String(invoice.grand_total ?? 0)) - parseFloat(String(invoice.amount_paid ?? 0)));
  const isPaid = activeStep === 2;
  const clientName = invoice.customer
    ? fmtCustomerName(invoice.customer, `Client #${invoice.customer_id}`)
    : `Client #${invoice.customer_id}`;

  // Pipeline step colors
  const PIPELINE = [
    { label: "Draft",     color: "#64748b", bg: "#f1f5f9" },
    { label: "Finalized", color: "#d97706", bg: "#fef3c7" },
    { label: "Paid",      color: "#16a34a", bg: "#dcfce7" },
  ];
  const currentPipeline = isPaid ? 2 : invoice.status >= 2 ? 1 : 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Grid container spacing={3}>

        {/* ── Full-width top header card ── */}
        <Grid item xs={12}>
          <Card
            sx={{
              bgcolor: "#ffffff",
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
              border: "1px solid #e2e8f0",
            }}
          >
            {/* Gradient accent bar */}
            <Box sx={{ height: 4, background: "linear-gradient(90deg, #f59e0b 0%, #ef4444 50%, #2f3891 100%)" }} />

            {/* Header content */}
            <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>

                {/* Left: invoice number + status + pipeline */}
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
                      onClick={() => navigate("/contract-invoice/list")}
                      sx={{ textTransform: "none", color: "#94a3b8", fontSize: 12, mr: 0.5, "&:hover": { bgcolor: "#f1f5f9" } }}
                    >
                      Back
                    </Button>
                    <Divider orientation="vertical" flexItem />
                    <Typography sx={{ color: "#0f172a", fontWeight: 800, fontSize: 22, letterSpacing: "-0.3px", lineHeight: 1 }}>
                      {invoice.contract_invoice_no}
                    </Typography>
                    {invoice.po_number && (
                      <Chip
                        label={`PO: ${invoice.po_number}`}
                        size="small"
                        sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600, fontSize: 10, border: "1px solid #e2e8f0", height: 20 }}
                      />
                    )}
                  </Box>

                  {/* Status + date */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <Chip
                      label={PIPELINE[currentPipeline].label}
                      size="small"
                      sx={{
                        fontWeight: 700, fontSize: 10, height: 22, px: 0.5,
                        color: PIPELINE[currentPipeline].color,
                        backgroundColor: PIPELINE[currentPipeline].bg,
                        border: `1px solid ${PIPELINE[currentPipeline].color}44`,
                      }}
                    />
                    {invoice.issue_date && (
                      <Typography sx={{ color: "#94a3b8", fontSize: 11 }}>
                        {dayjs(invoice.issue_date).format("MMM D, YYYY")}
                      </Typography>
                    )}
                  </Box>

                  {/* Workflow pipeline breadcrumb */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                    {PIPELINE.map((s, i) => {
                      const active = i === currentPipeline;
                      return (
                        <React.Fragment key={s.label}>
                          <Box sx={{
                            fontSize: "0.62rem", fontWeight: active ? 700 : 500,
                            color: active ? s.color : "#cbd5e1",
                            backgroundColor: active ? s.bg : "transparent",
                            borderRadius: "20px", px: active ? 1.25 : 0.5, py: "2px",
                            border: active ? `1px solid ${s.color}40` : "none",
                            lineHeight: 1.6,
                          }}>{s.label}</Box>
                          {i < 2 && <Box component="span" sx={{ color: "#e2e8f0", fontSize: "0.6rem" }}>▶</Box>}
                        </React.Fragment>
                      );
                    })}
                  </Box>

                  {/* Issued By badge */}
                  {invoice.company?.companyName && (() => {
                    const name: string = invoice.company.companyName;
                    const isBusLines = name.toLowerCase().includes("bus line");
                    return (
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, mt: 1 }}>
                        <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Issued by
                        </Typography>
                        <Box sx={{
                          display: "inline-flex", alignItems: "center",
                          px: 1.25, py: 0.4, borderRadius: "6px",
                          fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.02em",
                          bgcolor: isBusLines ? "#dbeafe" : "#dcfce7",
                          color: isBusLines ? "#1e40af" : "#15803d",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                        }}>
                          {name}
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>

                {/* Right: action buttons */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LocalPrintshopOutlinedIcon sx={{ fontSize: 13 }} />}
                    onClick={handlePrint}
                    sx={{ borderColor: "#e2e8f0", color: "#0f172a", textTransform: "none", fontWeight: 600, fontSize: 12, borderRadius: 1.5, "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" } }}
                  >
                    Print
                  </Button>
                  <Tooltip title="Edit invoice">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditSharpIcon sx={{ fontSize: 13 }} />}
                      onClick={() => navigate(`/contract-invoice/edit/${invoice.id}`)}
                      sx={{ borderColor: "#e2e8f0", color: "#2f3891", textTransform: "none", fontWeight: 600, fontSize: 12, borderRadius: 1.5, "&:hover": { borderColor: "#c7d2fe", bgcolor: "#eef2ff" } }}
                    >
                      Edit
                    </Button>
                  </Tooltip>
                  {isPaid ? (
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 2, px: 2, py: 0.75 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10b981", flexShrink: 0 }} />
                      <Typography sx={{ color: "#10b981", fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" }}>Payment Paid</Typography>
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AttachMoneyIcon sx={{ fontSize: 13 }} />}
                      onClick={() => {
                        setPayData({ date: new Date().toLocaleDateString("en-CA"), amount: "", method: "", description: "" });
                        setPayOpen(true);
                      }}
                      sx={{ bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" }, borderRadius: 1.5, textTransform: "none", fontWeight: 700, fontSize: 12, px: 2, boxShadow: "none" }}
                    >
                      Process Payment
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Divider */}
            <Box sx={{ mx: 3, borderTop: "1px solid #f1f5f9" }} />

            {/* Stepper */}
            <Box sx={{ px: 3, pt: 2.5, pb: 2, overflowX: "auto" }}>
              <Stepper activeStep={activeStep} alternativeLabel connector={<CustomStepConnector />}>
                {STEPS.map((label, index) => {
                  const isCompleted = activeStep > index;
                  const isActive = activeStep === index;
                  return (
                    <Step key={label}>
                      <StepLabel>
                        <Typography sx={{ fontWeight: isActive ? 700 : 600, fontSize: 12, color: isCompleted ? "#94a3b8" : isActive ? "#0f172a" : "#94a3b8", letterSpacing: 0.4, mt: 0.5 }}>
                          {label}
                        </Typography>
                        {isActive && (
                          <Typography sx={{ fontSize: 9, color: "#f59e0b", fontWeight: 700, mt: 0.25, letterSpacing: 0.4 }}>
                            IN PROGRESS
                          </Typography>
                        )}
                        {isCompleted && index === 2 && (
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#10b981", mt: 0.25 }}>
                            {fmt(invoice.amount_paid)}
                          </Typography>
                        )}
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>

            {/* Invoice number badge + payment action */}
            <Box sx={{ mx: 3, mb: 3, borderTop: "1px solid #f1f5f9", pt: 2.5, display: "flex", justifyContent: "center", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 2, px: 2, py: 0.75, flexShrink: 0 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#16a34a", letterSpacing: 0.5, whiteSpace: "nowrap" }}>INVOICE</Typography>
                <Box sx={{ width: "1px", height: 16, bgcolor: "#bbf7d0", flexShrink: 0 }} />
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#15803d", whiteSpace: "nowrap" }}>
                  #{invoice.contract_invoice_no}
                </Typography>
              </Box>
              {isPaid ? (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 2, px: 2, py: 0.75 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10b981", flexShrink: 0 }} />
                  <Typography sx={{ color: "#10b981", fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" }}>Payment Paid</Typography>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => {
                    setPayData({ date: new Date().toLocaleDateString("en-CA"), amount: "", method: "", description: "" });
                    setPayOpen(true);
                  }}
                  sx={{ bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" }, borderRadius: 2, textTransform: "none", fontWeight: 700, px: 4, py: 0.9, boxShadow: "0 2px 8px rgba(16,185,129,0.3)", fontSize: 13, letterSpacing: 0.3, flexShrink: 0 }}
                >
                  Process Payment
                </Button>
              )}
            </Box>
          </Card>
        </Grid>

        {/* ── RIGHT column: payment summary ── */}
        <Grid item lg={4} md={4} sm={12} xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            {/* Payment Summary */}
            <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, overflow: "hidden" }}>
              <Box sx={{ bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", px: 2.5, py: 1.5 }}>
                <Typography fontWeight={700} fontSize={14} color="#0f172a">Payment Summary</Typography>
              </Box>
              <Box sx={{ px: 2.5, py: 2 }}>
                {[
                  { label: "Invoice Total", value: fmt(invoice.grand_total), color: "#0f172a", weight: 600 },
                  { label: "Amount Paid",   value: fmt(invoice.amount_paid), color: "#16a34a",  weight: 600 },
                  { label: "Balance Due",   value: fmt(balance),             color: balance > 0 ? "#d97706" : "#16a34a", weight: 700 },
                ].map((row, i) => (
                  <Box
                    key={row.label}
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: i < 2 ? "1px solid #f1f5f9" : "none" }}
                  >
                    <Typography fontSize={13} color="text.secondary">{row.label}</Typography>
                    <Typography fontSize={14} fontWeight={row.weight} color={row.color}>{row.value}</Typography>
                  </Box>
                ))}

                {/* Progress bar */}
                {invoice.grand_total > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography fontSize={11} color="text.secondary">Payment progress</Typography>
                      <Typography fontSize={11} color="text.secondary">
                        {Math.min(100, Math.round((parseFloat(String(invoice.amount_paid ?? 0)) / parseFloat(String(invoice.grand_total))) * 100))}%
                      </Typography>
                    </Box>
                    <Box sx={{ height: 6, bgcolor: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                      <Box
                        sx={{
                          height: "100%",
                          width: `${Math.min(100, (parseFloat(String(invoice.amount_paid ?? 0)) / parseFloat(String(invoice.grand_total))) * 100)}%`,
                          bgcolor: isPaid ? "#16a34a" : "#2f3891",
                          borderRadius: 3,
                          transition: "width 0.4s ease",
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {!isPaid && (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AttachMoneyIcon />}
                    onClick={() => {
                      setPayData({ date: new Date().toLocaleDateString("en-CA"), amount: "", method: "", description: "" });
                      setPayOpen(true);
                    }}
                    sx={{ mt: 2.5, textTransform: "none", fontWeight: 700, borderRadius: 2, bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" } }}
                  >
                    Record Payment
                  </Button>
                )}
              </Box>
            </Paper>

            {/* Invoice meta */}
            <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, overflow: "hidden" }}>
              <Box sx={{ bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", px: 2.5, py: 1.5 }}>
                <Typography fontWeight={700} fontSize={14} color="#0f172a">Details</Typography>
              </Box>
              <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
                {[
                  { label: "Issue Date", value: invoice.issue_date ? dayjs(invoice.issue_date).format("MMM D, YYYY") : "—", icon: <CalendarTodayOutlinedIcon sx={{ fontSize: 13, color: "#94a3b8" }} /> },
                  { label: "Due Date",   value: invoice.due_date   ? dayjs(invoice.due_date).format("MMM D, YYYY")   : "—", icon: <CalendarTodayOutlinedIcon sx={{ fontSize: 13, color: "#94a3b8" }} /> },
                  { label: "Entries",    value: `${invoice.entries?.length ?? 0} billing row${(invoice.entries?.length ?? 0) !== 1 ? "s" : ""}`, icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 13, color: "#94a3b8" }} /> },
                ].map((row) => (
                  <Box key={row.label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      {row.icon}
                      <Typography fontSize={12} color="text.secondary">{row.label}</Typography>
                    </Box>
                    <Typography fontSize={12} fontWeight={600} color="#0f172a">{row.value}</Typography>
                  </Box>
                ))}

                {/* Totals breakdown */}
                <Divider sx={{ my: 0.5 }} />
                {[
                  { label: "Subtotal",   value: fmt(invoice.subtotal)   },
                  { label: "HST Tax",    value: fmt(invoice.tax_total)  },
                  ...(parseFloat(String(invoice.discount || 0)) > 0 ? [{ label: "Discount", value: `- ${fmt(invoice.discount)}` }] : []),
                ].map((row) => (
                  <Box key={row.label} sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography fontSize={12} color="text.secondary">{row.label}</Typography>
                    <Typography fontSize={12}>{row.value}</Typography>
                  </Box>
                ))}
                <Box sx={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #f1f5f9", pt: 1, mt: 0.5 }}>
                  <Typography fontSize={13} fontWeight={700}>Grand Total</Typography>
                  <Typography fontSize={13} fontWeight={700} color="#2f3891">{fmt(invoice.grand_total)}</Typography>
                </Box>
              </Box>
            </Paper>

          </Box>
        </Grid>

        {/* ── LEFT column: client + entries ── */}
        <Grid item lg={8} md={8} sm={12} xs={12}>
          <Grid container spacing={3}>

            {/* Client card */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.08)", border: "1px solid #f1f5f9" }}>
                <Box sx={{ p: 2.5, display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <Avatar
                    sx={{ width: 52, height: 52, bgcolor: "#ede9fe", color: "#2f3891", fontWeight: 700, fontSize: 18, flexShrink: 0 }}
                  >
                    {(invoice.customer?.customerFirstName?.[0] ?? "").toUpperCase()}
                    {(invoice.customer?.customerLastName?.[0] ?? "").toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: "#1e293b", fontSize: 15 }}>
                      {clientName}
                    </Typography>
                    {invoice.customer?.customerCompanyName && (
                      <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 500, mt: 0.25, mb: 1 }}>
                        {invoice.customer.customerCompanyName}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
                      {invoice.customer?.customerMobileNum && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", minWidth: 38, letterSpacing: 0.4 }}>MOB</Box>
                          <Typography sx={{ fontSize: 12, color: "#475569" }}>{invoice.customer.customerMobileNum}</Typography>
                        </Box>
                      )}
                      {invoice.customer?.customerEmail && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", minWidth: 38, letterSpacing: 0.4 }}>EMAIL</Box>
                          <Typography sx={{ fontSize: 12, color: "#475569" }}>{invoice.customer.customerEmail}</Typography>
                        </Box>
                      )}
                      {invoice.customer?.customerStreetName && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", minWidth: 38, letterSpacing: 0.4 }}>ADDR</Box>
                          <Typography sx={{ fontSize: 12, color: "#475569" }}>
                            {[invoice.customer.customerStreetNumber, invoice.customer.customerStreetName, invoice.customer.customerCity, invoice.customer.customerProvince].filter(Boolean).join(", ")}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Entries card */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.08)", border: "1px solid #f1f5f9" }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: "#2f3891" }} />
                  <Typography sx={{ fontWeight: 700, color: "#1e293b", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.8 }}>
                    Billing Entries
                  </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  {(invoice.entries ?? []).length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: "#94a3b8", textAlign: "center", py: 3 }}>
                      No billing entries
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {(invoice.entries ?? []).map((e, idx) => {
                        const lineTotal = parseFloat(String(e.total ?? 0));
                        const unitPrice = parseFloat(String(e.unit_price ?? 0));
                        const qty = parseFloat(String(e.quantity ?? 1));
                        const taxAmt = parseFloat(String(e.tax_amount ?? 0));
                        return (
                          <Box
                            key={idx}
                            sx={{ borderRadius: 2.5, border: "1px solid #e2e8f0", overflow: "hidden", bgcolor: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}
                          >
                            {/* Entry header */}
                            <Box sx={{ px: 2.5, py: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#2f3891", flexShrink: 0 }} />
                                <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>
                                  {e.description || `Entry ${idx + 1}`}
                                </Typography>
                                {e.entry_date && (
                                  <Chip
                                    label={dayjs(e.entry_date).format("MMM D, YYYY")}
                                    size="small"
                                    sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" }}
                                  />
                                )}
                              </Box>
                              <Typography sx={{ fontWeight: 800, fontSize: 16, color: "#2f3891" }}>
                                ${lineTotal.toFixed(2)}
                              </Typography>
                            </Box>

                            {/* Entry pricing grid */}
                            <Box sx={{ p: 2 }}>
                              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 1 }}>
                                <Box sx={{ textAlign: "center", bgcolor: "#dbeafe", borderRadius: 1.5, px: 1, py: 0.75 }}>
                                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: 0.4 }}>Qty</Typography>
                                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#1d4ed8" }}>{qty}</Typography>
                                </Box>
                                <Box sx={{ textAlign: "center", bgcolor: "#f0fdf4", borderRadius: 1.5, px: 1, py: 0.75 }}>
                                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#16a34a", textTransform: "uppercase", letterSpacing: 0.4 }}>Unit Price</Typography>
                                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#15803d" }}>${unitPrice.toFixed(2)}</Typography>
                                </Box>
                                <Box sx={{ textAlign: "center", bgcolor: "#fef3c7", borderRadius: 1.5, px: 1, py: 0.75 }}>
                                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#d97706", textTransform: "uppercase", letterSpacing: 0.4 }}>Tax</Typography>
                                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#d97706" }}>
                                    {e.tax_rate > 0 ? `${e.tax_rate}%` : "—"}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: "center", bgcolor: "#fff7ed", borderRadius: 1.5, px: 1, py: 0.75, border: "1px solid #fed7aa" }}>
                                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#c2410c", textTransform: "uppercase", letterSpacing: 0.4 }}>Total</Typography>
                                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#c2410c" }}>${lineTotal.toFixed(2)}</Typography>
                                </Box>
                              </Box>

                              {/* Pricing formula */}
                              {qty > 1 && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1.5, px: 1, py: 0.5, bgcolor: "#fafafa", borderRadius: 1, border: "1px dashed #e2e8f0" }}>
                                  <Typography sx={{ fontSize: 11, color: "#64748b" }}>{qty} × ${unitPrice.toFixed(2)}</Typography>
                                  <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>=</Typography>
                                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#2f3891" }}>${(qty * unitPrice).toFixed(2)}</Typography>
                                  {taxAmt > 0 && (
                                    <>
                                      <Box sx={{ width: "1px", height: 14, bgcolor: "#e2e8f0", mx: 0.5 }} />
                                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>HST ${taxAmt.toFixed(2)}</Typography>
                                    </>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>

                {/* Totals footer */}
                <Box sx={{ borderTop: "1px solid #f1f5f9", px: 2.5, py: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Box sx={{ minWidth: 240 }}>
                      {[
                        { label: "Subtotal", value: fmt(invoice.subtotal) },
                        { label: "HST Tax",  value: fmt(invoice.tax_total) },
                        ...(parseFloat(String(invoice.discount || 0)) > 0
                          ? [{ label: "Discount", value: `- ${fmt(invoice.discount)}` }]
                          : []),
                      ].map((row) => (
                        <Box key={row.label} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Typography fontSize={13} color="text.secondary">{row.label}</Typography>
                          <Typography fontSize={13}>{row.value}</Typography>
                        </Box>
                      ))}
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography fontWeight={700} fontSize={15}>Grand Total</Typography>
                        <Typography fontWeight={800} fontSize={16} color="#2f3891">{fmt(invoice.grand_total)}</Typography>
                      </Box>
                      {invoice.amount_paid > 0 && (
                        <>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.75 }}>
                            <Typography fontSize={13} color="#16a34a">Amount Paid</Typography>
                            <Typography fontSize={13} color="#16a34a" fontWeight={600}>{fmt(invoice.amount_paid)}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography fontWeight={700} fontSize={13} color="#d97706">Balance Due</Typography>
                            <Typography fontWeight={700} fontSize={13} color="#d97706">{fmt(balance)}</Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Memo / Notes */}
            {invoice.memo && (
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.08)", border: "1px solid #f1f5f9" }}>
                  <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: "#94a3b8" }} />
                    <Typography sx={{ fontWeight: 700, color: "#1e293b", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.8 }}>Notes</Typography>
                  </Box>
                  <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography sx={{ fontSize: 13, color: "#475569", whiteSpace: "pre-wrap" }}>{invoice.memo}</Typography>
                  </Box>
                </Card>
              </Grid>
            )}

          </Grid>
        </Grid>

      </Grid>

      {/* ── Payment dialog ── */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ height: 4, bgcolor: "#10b981" }} />
        <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Make a Payment</Typography>
          <IconButton size="small" onClick={() => setPayOpen(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </DialogTitle>
        <Box sx={{ px: 3, pb: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 1.5, px: 2, py: 1.25, display: "flex", flexDirection: "column", gap: 0.25 }}>
            {[
              { label: "Invoice",     value: invoice.contract_invoice_no },
              { label: "Grand Total", value: fmt(invoice.grand_total) },
              { label: "Paid",        value: fmt(invoice.amount_paid) },
              { label: "Balance Due", value: fmt(balance) },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography fontSize={12} color="text.secondary">{label}</Typography>
                <Typography fontSize={12} fontWeight={700}>{value}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Date</Typography>
              <TextField size="small" type="date" fullWidth value={payData.date}
                onChange={(e) => setPayData({ ...payData, date: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Amount</Typography>
              <TextField size="small" type="text" inputMode="decimal" fullWidth placeholder="0.00"
                value={payData.amount}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setPayData({ ...payData, amount: v });
                }} />
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Payment Method</Typography>
            <Select size="small" fullWidth displayEmpty value={payData.method}
              onChange={(e) => setPayData({ ...payData, method: e.target.value as string })}>
              <MenuItem value="" disabled><em>Select method</em></MenuItem>
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Credit Card">Credit Card</MenuItem>
              <MenuItem value="Debit Card">Debit Card</MenuItem>
              <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
              <MenuItem value="Cheque">Cheque</MenuItem>
              <MenuItem value="Purchase Order (PO)">Purchase Order (PO)</MenuItem>
              <MenuItem value="E-Transfer">E-Transfer</MenuItem>
            </Select>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Description</Typography>
            <TextField size="small" fullWidth multiline rows={2} placeholder="Optional note…"
              value={payData.description}
              onChange={(e) => setPayData({ ...payData, description: e.target.value })} />
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setPayOpen(false)} disabled={payingLocal}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSavePayment}
            disabled={payingLocal || !payData.amount || !payData.method}
            sx={{ textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: 1.5, bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" }, boxShadow: "none", minWidth: 80 }}>
            {payingLocal ? <CircularProgress size={18} color="inherit" /> : "Pay"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewContractInvoice;
